import React, { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import utilStyles from "../../asset/styles/util/util.module.scss"
import Table from "../../component/table/table"
import Loader from "../../component/loader/loaderComponent"
import DuplicateQuotationModal, { DuplicateQuotationSourceData } from "../../component/quotation/DuplicateQuotationModal"
import { buildSkippedHotelsRouteState, readSkippedHotels } from "component/quotation/duplicateQuotationSkippedHotelsUtil"
import { buildQuotationPreviewPath } from "util/quotationPreviewPathUtil"
import { agent } from "../../apiClient/api"
import {QuotationBasicDetailsModel, QuotationRemarkItemModel} from "../../models/axiosModel"
import {QuotationContext, QuotationContextType, useQuotation} from "context/quotationContext"
import MainTabsTableHeader, { FilterGroup } from "component/mainTabsTableHeader/mainTabsTableHeader"
import { cycleSort } from "../../util/listFilters"
import { useListView, LIST_VIEW_KEYS } from "../../context/listFilterContext"
import { TablePaginationBar } from "../../component/tablePagination"
import { isListPageSize, pageAfterPageSizeChange } from "../../component/tablePagination/listPagination"
import {
    QUOTATION_LIST_FILTER_KEYS,
    QUOTATION_LIST_SEARCH_DEBOUNCE_MS,
    buildQuotationListApiParams,
    buildQuotationListCityFilterOptions,
    resolveQuotationListOverflowPage,
} from "../../component/quotationList/quotationListQueryUtil"
import ListRefreshBar, { listRefreshStyles } from "../../component/listRefreshBar/listRefreshBar"
import { useDelayedFlag } from "../../component/listRefreshBar/useDelayedFlag"
import {IQuotationRow, IRemarkItem} from "../../component/quotationList/quotationListTypes";
import {
    buildQuotationStatusFilterOptions,
    buildQuotationStatusNameById,
    applyQuotationListRowFollowUpsChange,
    buildQuotationListMarkConfirmedLostStatusIds,
    buildQuotationListDraftStatusId,
    getGenericQuotationStatusRoot,
    mapQuotationListItemToRow,
    normalizeRemarks,
    quotationStatusIdByName,
} from "../../component/quotationList/quotationListUtil";
import ActivityHistoryPopup from "../../component/quotationStatusBadge/ActivityHistoryPopup";
import {
    canShowAllFollowUpTypeOptions,
    getGenericFollowUpTypes,
} from "../../component/quotationStatusBadge/activityHistoryUtil";
import {getColumnsArray} from "../../component/quotationList/quotationListComponents";
import QuotationRemarksPopup from "../../component/quotationList/QuotationRemarksPopup";
import QuotationDeleteConfirmPopup from "../../component/quotationList/QuotationDeleteConfirmPopup";
import MarkQuotationStatusConfirmPopup, {
    MarkQuotationStatusConfirmVariant,
} from "../../component/quotation/MarkQuotationStatusConfirmPopup";
import { buildQuotationStatusChangePayload } from "../../component/quotation/quotationStatusChangeUtil";
import { useSnackbar } from "../../context/snackbarContext";
import moment from "moment";

/** Background refreshes faster than this show no indicator at all (avoids flashing). */
const LIST_REFRESH_INDICATOR_DELAY_MS = 200

/** Primary: end − start in calendar days. Returns 0 when dates are missing/invalid. */
const resolveNumberOfNightsFromTravelDates = (
    startDate?: string | null,
    endDate?: string | null,
): number => {
    const start = moment(startDate)
    const end = moment(endDate)
    if (!start.isValid() || !end.isValid()) return 0
    return Math.max(0, end.startOf("day").diff(start.startOf("day"), "days"))
}

/** Primary → details API → 0 */
const resolveNumberOfNightsForDuplicate = async (row: IQuotationRow): Promise<number> => {
    // Primary: list row travel dates
    const fromDates = resolveNumberOfNightsFromTravelDates(row.start_date, row.end_date)
    if (fromDates > 0) return fromDates

    // Fallback: quotation details API
    if (row.quotation_id) {
        try {
            const detailsRes = await agent.QuotationDetailsAPI.get(row.quotation_id, true)
            const q: QuotationBasicDetailsModel | null = detailsRes?.status
                ? detailsRes.data?.quotation ?? null
                : null
            const fromDetails = Math.max(0, Number(q?.number_of_nights ?? 0))
            if (fromDetails > 0) return fromDetails
        } catch {
            // swallow — use last resort below
        }
    }

    // Last resort
    return 0
}

const Quotation: React.FC = () => {

    const navigate = useNavigate()
    const {
        search: searchText, setSearch: setSearchText,
        sortState, setSortState,
        filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
        page, setPage, pageSize, setPageSize,
    } = useListView(LIST_VIEW_KEYS.quotation)
    const {
        setQuotationStatusOptions,
        setQuotationFollowUpTypeOptions,
        quotationStatusOptions,
        quotationFollowUpTypeOptions,
    } = useQuotation();
    const { showError, showSuccess } = useSnackbar();

    const handleColumnSort = React.useCallback(
        (key: string) => setSortState((prev) => cycleSort(prev, key)),
        [setSortState],
    )

    const [quotationData, setQuotationData] = useState<IQuotationRow[]>([])
    const [quotationTotal, setQuotationTotal] = useState(0)
    const [listReloadToken, setListReloadToken] = useState(0)
    const [debouncedSearch, setDebouncedSearch] = useState(searchText.trim())
    const [statusFilterOptions, setStatusFilterOptions] = useState<{ label: string; value: string }[]>([])
    const [cityFilterOptions, setCityFilterOptions] = useState<{ label: string; value: string }[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false)
    const [selectedQuotationForDelete, setSelectedQuotationForDelete] = useState<IQuotationRow | null>(null)
    const [isRemarksPopupOpen, setIsRemarksPopupOpen] = useState(false)
    const [selectedQuotationForRemarks, setSelectedQuotationForRemarks] = useState<IQuotationRow | null>(null)
    const [remarksByQuotation, setRemarksByQuotation] = useState<Record<string, IRemarkItem[]>>({})
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
    const [isDuplicateSubmitting, setIsDuplicateSubmitting] = useState(false)
    const [duplicateSourceQuotationId, setDuplicateSourceQuotationId] = useState("")
    const [duplicateSourceData, setDuplicateSourceData] = useState<DuplicateQuotationSourceData | null>(null)
    const [markConfirmedLostStatusIds, setMarkConfirmedLostStatusIds] = useState<number[]>([3, 4])
    const [draftStatusId, setDraftStatusId] = useState(1)
    const [quotationStatusNameById, setQuotationStatusNameById] = useState<Map<number, string>>(new Map())
    const [confirmedStatusId, setConfirmedStatusId] = useState(5)
    const [lostStatusId, setLostStatusId] = useState(6)
    const [statusConfirmTarget, setStatusConfirmTarget] = useState<{
        variant: MarkQuotationStatusConfirmVariant
        row: IQuotationRow
    } | null>(null)
    const [isStatusChangeSubmitting, setIsStatusChangeSubmitting] = useState(false)
    const [activityHistoryRow, setActivityHistoryRow] = useState<IQuotationRow | null>(null)

    const { resetQuotationContext } = React.useContext(QuotationContext) as QuotationContextType;

    // List fetch reads this instead of depending on it, so generic-data arriving late doesn't refetch the list.
    const statusNameByIdRef = useRef<Map<number, string>>(new Map())
    const lastListQueryKeyRef = useRef<string | null>(null)
    // First load blocks the page; later sort/search/filter/page loads refresh in the background.
    const hasLoadedListRef = useRef(false)
    const [isListFetching, setIsListFetching] = useState(false)
    const isListRefreshing = useDelayedFlag(isListFetching, LIST_REFRESH_INDICATOR_DELAY_MS)

    // ----- Generic data: status / follow-up / city lookups (once) -----
    useEffect(() => {
        let isMounted = true

        const fetchGenericData = async () => {
            const genericResponse = await agent.GenericGlobalAPI.get(true).catch(() => null)
            if (!isMounted) return

            const genericRoot = getGenericQuotationStatusRoot(genericResponse)
            const statusNameById = buildQuotationStatusNameById(genericRoot)
            const quotationStatusData = genericResponse?.status ? genericResponse?.data[0].quotation_status : [];
            const genericCities = genericResponse?.status ? genericResponse?.data?.[0]?.city : [];

            statusNameByIdRef.current = statusNameById
            setQuotationStatusOptions(quotationStatusData);
            setQuotationFollowUpTypeOptions(getGenericFollowUpTypes(genericResponse));
            setStatusFilterOptions(buildQuotationStatusFilterOptions(genericRoot))
            setCityFilterOptions(buildQuotationListCityFilterOptions(genericCities))
            setMarkConfirmedLostStatusIds(buildQuotationListMarkConfirmedLostStatusIds(genericRoot))
            setDraftStatusId(buildQuotationListDraftStatusId(genericRoot))
            setQuotationStatusNameById(statusNameById)
            setConfirmedStatusId(quotationStatusIdByName(genericRoot, "confirmed") || 5)
            setLostStatusId(quotationStatusIdByName(genericRoot, "lost") || 6)
        }

        fetchGenericData()

        return () => {
            isMounted = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ----- Debounce search so each keystroke doesn't hit the API -----
    useEffect(() => {
        const nextSearch = searchText.trim()
        const timer = setTimeout(() => setDebouncedSearch(nextSearch), QUOTATION_LIST_SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchText])

    // ----- Server-side list: filter → search → sort → paginate all happen in the API -----
    useEffect(() => {
        // Search / filter / sort change → back to page 1. Setting page re-runs this effect, so skip fetching now.
        const queryKey = JSON.stringify({ search: debouncedSearch, filters: tableSelectedFilters, sortState })
        const queryChanged = lastListQueryKeyRef.current !== null && lastListQueryKeyRef.current !== queryKey
        lastListQueryKeyRef.current = queryKey
        if (queryChanged && page !== 1) {
            setPage(1)
            return
        }

        // Cleanup runs before the next fetch starts, so a cancelled run is always an older one.
        let isCancelled = false
        const isLatest = () => !isCancelled

        const fetchQuotationList = async () => {
            if (hasLoadedListRef.current) {
                setIsListFetching(true)
            } else {
                setIsLoading(true)
            }
            try {
                const response = await agent.QuotationListAPI.get(
                    buildQuotationListApiParams({
                        page,
                        pageSize,
                        search: debouncedSearch,
                        filters: tableSelectedFilters,
                        sortState,
                    }),
                    true,
                )
                if (!isLatest()) return

                const listData = response?.status ? response.data : null
                const listRows = Array.isArray(listData?.items) ? listData.items : []
                const total = Number(listData?.total) || 0

                const overflowPage = resolveQuotationListOverflowPage(page, total, pageSize)
                if (overflowPage) {
                    setPage(overflowPage)
                    return
                }

                setQuotationData(listRows.map((item) => mapQuotationListItemToRow(item, statusNameByIdRef.current)))
                setQuotationTotal(total)
                setRemarksByQuotation(listRows.reduce<Record<string, IRemarkItem[]>>((acc, item) => {
                    acc[item.quotation_id] = normalizeRemarks((item.remarks || []) as QuotationRemarkItemModel[])
                    return acc
                }, {}))
            } catch (error) {
                if (!isLatest()) return
                setQuotationData([])
                setQuotationTotal(0)
                setRemarksByQuotation({})
            } finally {
                if (isLatest()) {
                    hasLoadedListRef.current = true
                    setIsLoading(false)
                    setIsListFetching(false)
                }
            }
        }

        fetchQuotationList()

        return () => {
            // Invalidate this request so a slower, older response can't overwrite a newer one.
            isCancelled = true
        }
    }, [debouncedSearch, tableSelectedFilters, sortState, page, pageSize, listReloadToken, setPage])

    const reloadQuotationList = React.useCallback(() => setListReloadToken((token) => token + 1), [])

    const filterGroups: FilterGroup[] = useMemo(
        () => [
            {
                key: QUOTATION_LIST_FILTER_KEYS.city,
                label: "City",
                options: cityFilterOptions,
            },
            {
                key: QUOTATION_LIST_FILTER_KEYS.status,
                label: "Status",
                options: statusFilterOptions,
            },
        ],
        [cityFilterOptions, statusFilterOptions],
    )

    // ----- Pagination (server-side) -----
    const pageCount = pageSize > 0 ? Math.ceil(quotationTotal / pageSize) : 0

    const handlePageChange = React.useCallback(
        (nextPage: number) => {
            if (pageCount <= 0) return
            setPage(Math.min(Math.max(nextPage, 1), pageCount))
        },
        [pageCount, setPage],
    )

    const handlePageSizeChange = React.useCallback(
        (nextSize: number) => {
            if (!isListPageSize(nextSize)) return
            setPageSize(nextSize)
            setPage(pageAfterPageSizeChange(page, quotationTotal, nextSize))
        },
        [page, quotationTotal, setPage, setPageSize],
    )

    // A status change can move a row out of the current status filter / status sort position.
    const isListScopedByStatus = Boolean(tableSelectedFilters[QUOTATION_LIST_FILTER_KEYS.status]?.length)
        || sortState.key === "status"

    const handleDeleteClick = (row: IQuotationRow, event: React.MouseEvent) => {
        event.stopPropagation()
        setSelectedQuotationForDelete(row)
        setIsDeletePopupOpen(true)
    }

    const handleCancelDelete = () => {
        setIsDeletePopupOpen(false)
        setSelectedQuotationForDelete(null)
    }

    const handleCloseRemarks = () => {
        setIsRemarksPopupOpen(false)
        setSelectedQuotationForRemarks(null)
    }

    const handleConfirmDelete = async () => {
        const row = selectedQuotationForDelete
        if (!row?.quotation_id) return
        try {
            setIsLoading(true)
            const response = await agent.QuotationDeleteAPI.delete(row.quotation_id, true)
            if (response?.status) {
                // Refetch so the page backfills from the next page and total stays correct.
                reloadQuotationList()
            }
        } finally {
            setIsLoading(false)
            handleCancelDelete()
        }
    }

    // const handleDuplicateClick = async (row: IQuotationRow, event: React.MouseEvent) => {
    //     event.stopPropagation()
    //     if (!row?.quotation_id) return
    //     setIsDuplicateSubmitting(true)
    //     try {
    //         const detailsRes = await agent.QuotationDetailsAPI.get(row.quotation_id, true)
    //         const data = detailsRes?.status ? detailsRes.data : null
    //         const q: QuotationBasicDetailsModel | undefined | null = data?.quotation || null
    //         if(q) {
    //             const mealGuideFlags = readDuplicateSourceMealGuideFlags(data, q)
    //             setDuplicateSourceQuotationId(row.quotation_id)
    //             setDuplicateSourceData({
    //                 agency_id: q?.agency_id || "",
    //                 agent_id: q?.agent_id || "",
    //                 foc_pax_number: Number(q?.foc_pax_number || 0) || 0,
    //                 number_of_adults: Number(q?.number_of_adults || 0) || 0,
    //                 number_of_children: Number(q?.number_of_children || 0) || 0,
    //                 start_date: q?.start_date || "",
    //                 number_of_nights: Number(q?.number_of_nights || 0) || 0,
    //                 flag_with_tip: mealGuideFlags.flag_with_tip,
    //                 flag_all_days_have_dinner: mealGuideFlags.flag_all_days_have_dinner,
    //                 quotation_status_id: q.quotation_status_id,
    //             })
    //         }
    //         setIsDuplicateModalOpen(true)
    //     } finally {
    //         setIsDuplicateSubmitting(false)
    //     }
    // }
    const handleDuplicateClick = async (row: IQuotationRow, event: React.MouseEvent) => {
        event.stopPropagation()
        if (!row?.quotation_id) return

        setIsDuplicateSubmitting(true)
        try {
            const numberOfNights = await resolveNumberOfNightsForDuplicate(row)
            setDuplicateSourceQuotationId(row.quotation_id)
            setDuplicateSourceData({
                agency_id: row.agency_id,
                agent_id: row.agent_id,
                foc_pax_number: row.foc_pax_number,
                flag_all_days_have_dinner: row.flag_all_days_have_dinner,
                flag_with_tip: row.flag_with_tip,
                number_of_adults: row.number_of_adults,
                number_of_children: row.number_of_children,
                number_of_nights: numberOfNights,
                start_date: row.start_date,
                quotation_status_id: row.quotation_status_id,
            })
            setIsDuplicateModalOpen(true)
        } finally {
            setIsDuplicateSubmitting(false)
        }
    }

    const handleDuplicateConfirm = async (payload: any) => {
        if (!duplicateSourceQuotationId) return
        setIsDuplicateSubmitting(true)
        try {
            const res = await agent.QuotationDuplicateAPI.post(duplicateSourceQuotationId, payload, true)
            const newQuotationId = res?.status ? res?.data?.quotation_id : ""
            if (newQuotationId) {
                setIsDuplicateModalOpen(false)
                // Preview shows the Hotel Availability Update popup from this state.
                navigate(buildQuotationPreviewPath(newQuotationId), {
                    state: buildSkippedHotelsRouteState(readSkippedHotels(res)),
                })
            }
        } finally {
            setIsDuplicateSubmitting(false)
        }
    }

    const handleFollowUpsChange = React.useCallback((quotationId: string, followUps: IQuotationRow["follow_ups"]) => {
        setQuotationData((prev) =>
            prev.map((row) =>
                row.quotation_id === quotationId
                    ? applyQuotationListRowFollowUpsChange(row, followUps, quotationStatusOptions)
                    : row,
            ),
        )
        // Saving a follow-up can move Sent → Follow Up.
        if (isListScopedByStatus) reloadQuotationList()
    }, [quotationStatusOptions, isListScopedByStatus, reloadQuotationList])

    const handleMarkQuotationStatus = async (
        row: IQuotationRow,
        nextStatusId: number,
        successMessage: string,
        remark?: string,
    ) => {
        if (!row?.quotation_id || !nextStatusId) return

        setIsStatusChangeSubmitting(true)
        try {
            const response = await agent.QuotationChangeStatusAPI.put(
                buildQuotationStatusChangePayload(
                    row.quotation_id,
                    nextStatusId,
                    remark,
                ),
                true,
            )

            if (!response?.status) {
                showError("Failed to update quotation status")
                return
            }

            const nextStatusName = quotationStatusNameById.get(nextStatusId) || row.status
            const statusChangedAt = response.data?.quotation_status_changed_at ?? null
            const statusRemark = response.data?.remark ?? remark ?? null
            setQuotationData((prev) =>
                prev.map((item) =>
                    item.quotation_id === row.quotation_id
                        ? {
                            ...item,
                            status_id: nextStatusId,
                            status: nextStatusName,
                            quotation_status_changed_at: nextStatusId === confirmedStatusId
                                ? (statusChangedAt || item.quotation_status_changed_at)
                                : item.quotation_status_changed_at,
                            remark: nextStatusId === lostStatusId
                                ? String(statusRemark ?? "")
                                : item.remark,
                        }
                        : item,
                ),
            )
            setStatusConfirmTarget(null)
            showSuccess(successMessage)
            if (isListScopedByStatus) reloadQuotationList()
        } catch {
            showError("Failed to update quotation status")
        } finally {
            setIsStatusChangeSubmitting(false)
        }
    }

    const handleMarkConfirmedClick = React.useCallback(
        (row: IQuotationRow, event: React.MouseEvent) => {
            event.stopPropagation()
            setStatusConfirmTarget({ variant: "confirmed", row })
        },
        [],
    )

    const handleMarkLostClick = React.useCallback(
        (row: IQuotationRow, event: React.MouseEvent) => {
            event.stopPropagation()
            setStatusConfirmTarget({ variant: "lost", row })
        },
        [],
    )

    const openActivityHistoryForRow = React.useCallback((row: IQuotationRow) => {
        setActivityHistoryRow(row)
    }, [])

    const handleConfirmStatusChange = (remark?: string) => {
        if (!statusConfirmTarget) return

        const { variant, row } = statusConfirmTarget
        if (variant === "confirmed") {
            handleMarkQuotationStatus(row, confirmedStatusId, "Quotation marked as confirmed")
            return
        }

        handleMarkQuotationStatus(row, lostStatusId, "Quotation marked as lost", remark ?? "")
    }

    const activityHistoryRowLive = useMemo(() => {
        if (!activityHistoryRow) return null
        return quotationData.find((row) => row.quotation_id === activityHistoryRow.quotation_id)
            || activityHistoryRow
    }, [activityHistoryRow, quotationData])

    const activityHistoryAllowAllFollowUpTypes = activityHistoryRowLive
        ? canShowAllFollowUpTypeOptions(
            activityHistoryRowLive.status_id,
            activityHistoryRowLive.status,
            quotationStatusOptions,
        )
        : false

    const columns = getColumnsArray(
        handleDuplicateClick,
        handleDeleteClick,
        handleFollowUpsChange,
        handleMarkConfirmedClick,
        handleMarkLostClick,
        openActivityHistoryForRow,
        markConfirmedLostStatusIds,
        draftStatusId,
    )

    return (
        <>
            {isLoading && <Loader message="Loading quotations..." overlay={true} />}
            <QuotationDeleteConfirmPopup
                isOpen={isDeletePopupOpen}
                quotationName={selectedQuotationForDelete?.quotation_name}
                staCode={selectedQuotationForDelete?.sta_code}
                isSubmitting={isLoading}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
            />
            <MarkQuotationStatusConfirmPopup
                isOpen={Boolean(statusConfirmTarget)}
                variant={statusConfirmTarget?.variant || "confirmed"}
                staCode={statusConfirmTarget?.row.sta_code || ""}
                isSubmitting={isStatusChangeSubmitting}
                onCancel={() => setStatusConfirmTarget(null)}
                onConfirm={handleConfirmStatusChange}
            />
            <ActivityHistoryPopup
                isOpen={Boolean(activityHistoryRowLive)}
                quotationId={activityHistoryRowLive?.quotation_id || ""}
                staCode={activityHistoryRowLive?.sta_code || ""}
                startedAt={activityHistoryRowLive?.created_at}
                createdAt={activityHistoryRowLive?.ready_to_send_at}
                quotationDownloads={activityHistoryRowLive?.quotation_downloads}
                followUps={activityHistoryRowLive?.follow_ups || []}
                followUpTypes={quotationFollowUpTypeOptions}
                allowAllFollowUpTypes={activityHistoryAllowAllFollowUpTypes}
                onClose={() => setActivityHistoryRow(null)}
                onFollowUpsChange={handleFollowUpsChange}
            />
            <QuotationRemarksPopup
                isOpen={isRemarksPopupOpen}
                quotation={selectedQuotationForRemarks}
                remarksByQuotation={remarksByQuotation}
                onRemarksByQuotationChange={setRemarksByQuotation}
                onClose={handleCloseRemarks}
            />
            <DuplicateQuotationModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                source={duplicateSourceData}
                isSubmitting={isDuplicateSubmitting}
                onConfirm={handleDuplicateConfirm}
            />
            <div className={`${utilStyles.pageContainer} ${utilStyles.listPageFill}`}>
                <div className={utilStyles.listPageHeader}>
                    <MainTabsTableHeader
                        searchValue={searchText}
                        onSearchChange={setSearchText}
                        searchPlaceholder="Search by ID, name, or agent..."
                        totalCount={quotationTotal}
                        filteredCount={quotationTotal}
                        itemLabel="quotations"
                        sortValue=""
                        onSortChange={() => undefined}
                        sortOptions={[]}
                        filterGroups={filterGroups}
                        selectedFilters={tableSelectedFilters}
                        onFiltersChange={setTableSelectedFilters}
                        addButtonLabel="Quotation"
                        onAddClick={() => {
                            resetQuotationContext()
                            navigate("/quotation/new")
                        }}
                        showResultCount={false}
                    />
                </div>
                <div className={`${utilStyles.listPageTableBlock} ${listRefreshStyles.refreshHost}`}>
                    <ListRefreshBar active={isListRefreshing} />
                    <div className={utilStyles.listPageTableScroll}>
                        <Table
                            columns={columns}
                            data={quotationData}
                            containerClassName={isListRefreshing ? listRefreshStyles.tableRefreshing : undefined}
                            keyExtractor={(row, index) => `${row.quotation_id}-${index}`}
                            onRowClick={(row) => {
                                navigate(buildQuotationPreviewPath(row.quotation_id))
                            }}
                            emptyMessage={isLoading || isListFetching ? "Loading quotations..." : "No quotations available"}
                            minWidth={1200}
                            fillHeight={true}
                            sortKey={sortState.key}
                            sortDirection={sortState.direction}
                            onSort={handleColumnSort}
                        />
                    </div>
                    <TablePaginationBar
                        total={quotationTotal}
                        page={pageCount > 0 ? Math.min(page, pageCount) : page}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </div>
            </div>
        </>
    )
}

export default Quotation
